/**
 * `/privacy` — the INTERIM privacy notice (issue #449, partial).
 *
 * WHY "INTERIM". #449 asks for a full user-facing privacy statement — one
 * document covering everything the app holds, who can see it, how long it
 * is kept, and how a guardian asks for correction or deletion. A complete
 * draft exists (`docs/privacy-statement.md`, PR #496) but it is pending
 * review by Andreas and Martin, because retention periods, the contact
 * point, and the #340 admin-posture question are decisions for them, not
 * an agent's guess.
 *
 * Andreas's ruling, 2026-09-09: ship a SHORT, HONEST notice now, before
 * the pilot's ~20 players arrive, rather than wait for every open question
 * to close. This module is that notice, not the full statement — #449
 * stays open.
 *
 * THE RULE THIS FILE FOLLOWS: describe what is true, promise nothing.
 * There is no retention period or deletion SLA anywhere below, because
 * none has been decided — see `docs/privacy-statement.md`'s own retention
 * section and issue #449 for why that is a principal decision, not this
 * notice's to invent. The decided periods are the 90-day backup horizon
 * (D199.1, #1032), stated in `DELETION_AND_CONTACT`'s first paragraph, the
 * 90-day detailed usage counts (D218), the 14-day failed-request record
 * (D234, #1191), `ERROR_RECORD_SENTENCE`, and the 400-day administrator-action
 * record (#1440, D373/D375), `ADMIN_ACTION_RECORD_SENTENCE`.
 * `privacyNotice.test.js` enforces this with a negative test: it fails the
 * build if a digit-plus-day/month/year pattern other than those
 * sentences, or the phrase "we will never", is ever added here.
 *
 * EVERY CATEGORY BELOW IS VERIFIED AGAINST THE SCHEMA, NOT COPIED FROM
 * #449'S BODY. #449 listed a starting set from before #456 (private
 * mobile number) and before #505 confirmed a player can hold more than
 * one active coach at once; both are reflected here. Sources: read
 * 2026-09-10 from `api/src/db/schema.ts` (all tables), `api/src/routes/*`,
 * `api/src/lib/mobilePhone.ts`, `api/src/routes/playerHealth.ts`,
 * `api/src/routes/identitySharing.ts`, `api/src/routes/checkins.ts`,
 * `api/src/routes/adminUsers.ts`, `api/src/lib/github.ts`,
 * `api/src/lib/sendMail.ts`, `api/src/index.ts` (mounted routes),
 * `frontend/src/pages/Register.jsx`, `frontend/src/lib/nearMe.js`.
 */

export const PRIVACY_NOTICE_TITLE = "What Cricket Compass holds about you or your child";

export const PRIVACY_NOTICE_INTRO = [
  "This is an interim notice, published ahead of the pilot. It says, in plain " +
    "terms, what the app holds about a player, who can see it, what leaves our " +
    "systems, and how to ask us about any of it.",
  "It is not yet the complete privacy statement #449 asks for — that needs " +
    "decisions (a retention schedule, a named contact point, and how deletion " +
    "requests are handled) that have not been made yet. Rather than write " +
    "something that sounds complete and isn't, this page says only what is " +
    "true today, and says plainly where the gaps are.",
];

/**
 * The one sentence D234 decided for the failed-request record (#1191). Kept
 * as a constant so `privacyNotice.test.js` can name it as the second decided
 * retention sentence, beside the 90-day backup one.
 */
export const ERROR_RECORD_SENTENCE =
  "We keep a record of failed requests (no personal details) for 14 days to fix problems.";

/**
 * The one sentence for the administrator-action record (#1440, D373; ruled
 * D375). Word for word the sentence in `docs/privacy-statement.md`;
 * `privacyNotice.test.js` checks both.
 */
export const ADMIN_ACTION_RECORD_SENTENCE =
  "We keep a record of administrator password resets, sign-in email changes, role grants (with the role given), administrator-created accounts and sign-in-link sign-ins for 400 days: the time and the accounts involved, no personal details.";

/**
 * The full data inventory. `id` is stable (used by tests and anchors);
 * `label` is the heading; `detail` is the one-paragraph description shown
 * on the page. Order follows how directly a category touches the player.
 */
export const DATA_CATEGORIES = [
  {
    id: "account",
    label: "Your account",
    detail:
      "Your name, email address, and a password stored only as a one-way " +
      "cryptographic hash — we cannot read it back. The account also records " +
      "whether it is a player, coach or admin account.",
  },
  {
    id: "mobile",
    label: "Mobile number",
    detail:
      "If you add one in Settings, your mobile number is stored against your " +
      "account and is private to you — it is not shown to a linked coach, an " +
      "administrator, or on any roster or search result. You are the only " +
      "reader of it.",
  },
  {
    id: "player-profile",
    label: "Player profile",
    detail:
      "Full name, date of birth, club, team, country, batting and bowling " +
      "style, playing role, and an optional profile photo. The date of birth " +
      "also decides whether a parent or guardian holds the account's sign-in " +
      "(see \"Children and guardians\" below).",
  },
  {
    id: "matches-training",
    label: "Match and training history, with locations",
    detail:
      "Every match you log — opponent, competition, ground, date, and the " +
      "full scorecard — and every training session, including where it was " +
      "held, what was practised, and your own self-rated confidence, focus " +
      "and motivation for it.",
  },
  {
    id: "goals",
    label: "Goals and development plans",
    detail:
      "Goals you set yourself, or a coach assigns to you, with progress and " +
      "notes. When a coach assigns a goal, we record which coach did.",
  },
  {
    id: "reflections-checkins",
    label: "Reflections and check-ins",
    detail:
      "Free-text writing by the player: match and season reflections, and a " +
      "monthly self-assessment check-in with a self-rating. This is personal " +
      "writing, often by a child — see \"Who can see what\" for who does and " +
      "does not see it.",
  },
  {
    id: "identity-vision",
    label: "Identity & Vision answers",
    detail:
      "The player's own answers about their cricketing vision, strengths and " +
      "weaknesses, game identity, and mental performance prompts with " +
      "self-scores. Nothing here is shared with a coach unless the player " +
      "turns sharing on for that section.",
  },
  {
    id: "coach-feedback",
    label: "Coach feedback, and coach-private notes",
    detail:
      "Feedback a coach writes to the player, visible to the player with the " +
      "coach's name on it — and, kept separately, a coach's own private " +
      "observations about the player, which are not shown to the player.",
  },
  {
    id: "health",
    label: "Health and emergency contact details",
    detail:
      "If entered: an emergency contact's name, relationship and phone " +
      "number, medical conditions and allergies, and accessibility needs. " +
      "This is the most sensitive record the app holds, and it is only ever " +
      "entered — nothing here is required to use the app.",
  },
  {
    id: "coach-links",
    label: "Coach–player links",
    detail:
      "Which coaches a player has invited or linked with, and which coach " +
      "assigned which guide or goal. A player can hold more than one linked " +
      "coach at a time, and can revoke any link at any time.",
  },
  {
    id: "grounds",
    label: "Saved and shared grounds",
    detail:
      "Grounds you bookmark, and any ground or facility you add to the " +
      "shared directory (with a record of who added it). Your device's own " +
      "location, used to find grounds near you, never leaves your device.",
  },
  {
    id: "feedback-form",
    label: "Feedback you send us",
    detail:
      "If you use the in-app \"send feedback\" form, we keep your message " +
      "together with your display name, your role, which screen you were " +
      "on, the app version, and your browser's user-agent string.",
  },
  {
    id: "sessions",
    label: "Sign-in and password-reset records",
    detail:
      "A sign-in session record so you stay logged in, and — if you ask for " +
      "a password reset — a short-lived reset record. Both store only a " +
      "cryptographic hash of their token, never the token itself.",
  },
  {
    id: "coach-account",
    label: "Coach accounts",
    detail:
      "A coach account additionally holds an optional self-written coaching " +
      "profile: headline, experience, qualifications, specialisms, region, " +
      "and an optional photo. Every field on it is the coach's own claim, " +
      "and the app says so where it is shown.",
  },
  {
    id: "usage-counts",
    label: "Usage counts",
    detail:
      "Counts of which screens you open, which buttons you use and which steps you finish, with your " +
      "role (player or coach), the kind of device, the time, and a random " +
      "code for your account instead of your name. Never anything you typed. " +
      "The detailed counts are kept 90 days, then only totals with no code " +
      "remain. You can switch the counting off in Settings, under " +
      "\"What we measure\".",
  },
  {
    id: "error-record",
    label: "Failed-request record",
    // The sentence D234 decided (Andreas, 2026-10-03, #1191), word for word.
    detail: ERROR_RECORD_SENTENCE,
  },
  {
    id: "admin-action-record",
    label: "Administrator-action record",
    // The sentence D375 ruled (#1440), word for word.
    detail: ADMIN_ACTION_RECORD_SENTENCE,
  },
];

export const WHO_CAN_SEE = [
  "You see everything about yourself except a coach's private observations, " +
    "and your mobile number is visible to nobody else at all.",
  "A coach only sees a player's data after the player (or their guardian) " +
    "links them, and the player can revoke that link at any time. A player " +
    "can be linked with more than one coach at once, and each sees the same " +
    "profile, matches, training log, goals and shared feedback.",
  "Identity & Vision sections are shared only if the player turns sharing on " +
    "for that section, and sharing reaches every one of the player's currently " +
    "linked coaches, not a single chosen coach.",
  "The health and emergency contact record, if entered, is visible to all of " +
    "the player's linked coaches and only them — it is deliberately " +
    "all-or-nothing, because a partial record a coach believes is complete " +
    "is more dangerous in an emergency than no record.",
  "No coach, and no administrator, ever sees a player's monthly check-ins — " +
    "those stay private to the player.",
  "A small number of administrator accounts, held by the people who run the " +
    "platform, can see the account list (including players' dates of birth), " +
    "player profiles, and most cricket data. An administrator cannot see the " +
    "health record or a player's check-ins through any admin screen.",
];

export const CHILDREN_AND_GUARDIANS = [
  "Cricket Compass is built for junior and club cricket. The account is " +
    "always the player's, in the player's name.",
  "Under 11, a parent or guardian sets the account up and holds the sign-in " +
    "details — the email on the account is the guardian's own.",
  "From 11 to 17, the player holds their own sign-in, and the sign-up form " +
    "tells the family that a guardian should know the account exists and " +
    "approves the links it makes.",
];

export const WHAT_LEAVES = [
  "Resend, our email provider, sends password-reset emails, sign-in-link " +
    "emails, and coach-invite notifications — always to an address already " +
    "on an account, and never with your free-text content in them.",
  "GitHub receives a copy of anything sent through the in-app feedback " +
    "form, filed as an issue in the project's private issue tracker, " +
    "carrying your display name, role, message, screen and app version.",
  "Cloudflare hosts the whole application — the app, the database, and the " +
    "usage counts — in the project's own account.",
  "If you use \"Talk instead of typing\", your recording goes to Cloudflare " +
    "Workers AI, a speech-to-text service in the same Cloudflare account " +
    "that hosts the app. It turns the recording into text and sends the " +
    "text back. We then delete the recording. We don't keep it, and we " +
    "don't listen to it. Only the words you save are kept, the same as if " +
    "you had typed them.",
  "No other AI service receives any of your data. No third-party " +
    "advertising or analytics script runs in the app.",
];

export const DELETION_AND_CONTACT = [
  "Settings › Delete your account removes your name and contact details at " +
    "once and signs you out everywhere. Your match figures stay with no name " +
    "on them. Backups are kept for 90 days and then expire.",
  "To ask what we hold about you or your child, ask for it to be corrected, " +
    "or ask for an account to be removed, contact your club or team — the " +
    "people running your pilot know how to reach the people who operate " +
    "this app. If you already have an account, you can also use Settings → " +
    "Send feedback.",
  "How long records are kept while an account exists has not been settled " +
    "yet. We are not going to guess at an answer here — the full statement, " +
    "once that decision is made, will say.",
];

/**
 * What the FLAG-OFF notice (`pages/PrivacyNotice.jsx`) keeps saying, byte for
 * byte as it did before #1032 (review ruling R5): the shipped Settings page
 * has no delete card, so the shipped notice must not point at one. Only the
 * redrawn notice (`PrivacyNoticeNew.jsx`) reads `DELETION_AND_CONTACT` and
 * `COMING_LATER` above and below. Delete these with the shipped page.
 */
export const DELETION_AND_CONTACT_SHIPPED = [
  "There is no self-service \"delete my account\" button yet. Building one " +
    "is tracked separately (issue #502).",
  "To ask what we hold about you or your child, ask for it to be corrected, " +
    "or ask for an account to be removed, contact your club or team — the " +
    "people running your pilot know how to reach the people who operate " +
    "this app. If you already have an account, you can also use Settings → " +
    "Send feedback.",
  "How long records are kept, and what a full deletion means for backups, " +
    "have not been settled yet. We are not going to guess at an answer here " +
    "— the full statement, once those decisions are made, will say.",
];

export const COMING_LATER_SHIPPED =
  "A complete privacy statement — covering retention, a named contact " +
  "point, and self-service deletion — is being finished and will replace " +
  "this page. Issue #449 tracks that work.";

export const COMING_LATER =
  "A complete privacy statement — covering retention and a named contact " +
  "point — is being finished and will replace " +
  "this page. Issue #449 tracks that work.";
